import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Ship, Anchor, Warehouse, Clock, Container, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import XenetaVsNac from "./XenetaVsNac";

const cur = (c) => ({ USD: "US$", AUD: "A$", SGD: "S$", NZD: "NZ$", CNY: "¥", EUR: "€" }[c] || (c ? `${c} ` : ""));
const isNum = (v) => typeof v === "number";
const numOr = (v) => (isNum(v) ? v : 0);
const money = (n, c) => `${cur(c)}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const aud = (n) => `A$${Math.round(n).toLocaleString()}`;

const sectionSubtotal = (charges, size) => charges.reduce((a, ch) => a + numOr(size === "20" ? ch.v20 : ch.v40), 0);

const Amt = ({ v, ccy, active }) => {
  if (v == null) return <span className="text-muted-foreground/40">—</span>;
  if (isNum(v)) return <span className={`tabular-nums ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{money(v, ccy)}</span>;
  return <span className="text-[11px] italic text-muted-foreground">{v}</span>;
};

const Term = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 text-xs py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground/80">{value}</span>
    </div>
  );
};

const Block = ({ title, icon: Icon, section, size, testid, terms }) => {
  const st20 = sectionSubtotal(section.charges, "20");
  const st40 = sectionSubtotal(section.charges, "40");
  const ccy = section.currency;
  return (
    <div className="border border-border bg-white flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-slate-50/70">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#0072B8]" strokeWidth={1.75} />
          <span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold text-foreground">{title}</span>
        </div>
        <span className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground border border-border px-2 py-0.5 rounded">{ccy || "—"}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="px-4 py-2 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Charge</th>
              <th className={`px-4 py-2 font-mono-plex text-[10px] tracking-widest uppercase font-semibold text-right ${size === "20" ? "text-[#0072B8]" : "text-muted-foreground"}`}>20′</th>
              <th className={`px-4 py-2 font-mono-plex text-[10px] tracking-widest uppercase font-semibold text-right ${size === "40" ? "text-[#0072B8]" : "text-muted-foreground"}`}>40′</th>
            </tr>
          </thead>
          <tbody>
            {section.charges.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-4 text-xs text-muted-foreground text-center">No charges on file</td></tr>
            )}
            {section.charges.map((ch, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`${testid}-row-${i}`}>
                <td className="px-4 py-2.5 text-foreground/90">
                  {ch.label}
                  {ch.basis === "shipment" && <span className="ml-1.5 font-mono-plex text-[9px] tracking-widest uppercase text-muted-foreground/70">/ shipment</span>}
                </td>
                <td className={`px-4 py-2.5 text-right ${size === "20" ? "bg-[#0072B8]/[0.05]" : ""}`}><Amt v={ch.v20} ccy={ccy} active={size === "20"} /></td>
                <td className={`px-4 py-2.5 text-right ${size === "40" ? "bg-[#0072B8]/[0.05]" : ""}`}><Amt v={ch.v40} ccy={ccy} active={size === "40"} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border">
              <td className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Subtotal</td>
              <td className={`px-4 py-3 text-right tabular-nums font-semibold ${size === "20" ? "text-[#0072B8] bg-[#0072B8]/[0.05]" : "text-muted-foreground"}`}>{money(st20, ccy)}</td>
              <td className={`px-4 py-3 text-right tabular-nums font-semibold ${size === "40" ? "text-[#0072B8] bg-[#0072B8]/[0.05]" : "text-muted-foreground"}`}>{money(st40, ccy)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {terms && terms.some((t) => t.value != null && t.value !== "") && (
        <div className="px-5 py-3 border-t border-border mt-auto">
          <div className="font-mono-plex text-[9px] tracking-widest uppercase text-muted-foreground/70 mb-1">Terms</div>
          {terms.map((t, i) => <Term key={i} label={t.label} value={t.value} />)}
        </div>
      )}
    </div>
  );
};

const NacRates = ({ data }) => {
  const { lanes, fx, fx_meta } = data;
  const [origin, setOrigin] = useState(lanes[0]?.origin_code || "");
  const [destination, setDestination] = useState(lanes[0]?.destination_code || "");
  const [size, setSize] = useState("40");

  const origins = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => (seen.has(l.origin_code) ? false : seen.add(l.origin_code)))
      .map((l) => ({ code: l.origin_code, name: l.origin_name }));
  }, [lanes]);
  const destinations = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => l.origin_code === origin)
      .filter((l) => (seen.has(l.destination_code) ? false : seen.add(l.destination_code)))
      .map((l) => ({ code: l.destination_code, name: l.destination_name }));
  }, [lanes, origin]);

  const onOrigin = (v) => {
    setOrigin(v);
    const first = lanes.find((l) => l.origin_code === v);
    if (first) setDestination(first.destination_code);
  };

  const lane = lanes.find((l) => l.origin_code === origin && l.destination_code === destination);

  const SizeToggle = () => (
    <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="ratecard-size-toggle">
      {["20", "40"].map((s) => (
        <button key={s} onClick={() => setSize(s)} data-testid={`ratecard-size-${s}`}
          className={`px-4 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${size === s ? "bg-[#0072B8] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}>
          {s}′ {s === "20" ? "GP" : "HC"}
        </button>
      ))}
    </div>
  );

  const grand = useMemo(() => {
    if (!lane) return null;
    const o = sectionSubtotal(lane.origin.charges, size);
    const f = sectionSubtotal(lane.ocean.charges, size);
    const d = sectionSubtotal(lane.destination.charges, size);
    const oc = lane.origin.currency, fc = lane.ocean.currency, dc = lane.destination.currency;
    const oA = o * (fx[oc] ?? 0), fA = f * (fx[fc] ?? 0), dA = d * (fx[dc] ?? 0);
    return { o, f, d, oc, fc, dc, oA, fA, dA, total: oA + fA + dA };
  }, [lane, size, fx]);

  return (
    <div data-testid="nac-rates">
      <p className="text-sm text-muted-foreground max-w-3xl mb-6">{data.meta.subtitle}</p>

      <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 items-end" data-testid="ratecard-lookup">
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Origin</label>
          <Select value={origin} onValueChange={onOrigin}>
            <SelectTrigger data-testid="ratecard-origin" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{origins.map((o) => <SelectItem key={o.code} value={o.code}>{o.name} ({o.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Destination</label>
          <Select value={destination} onValueChange={setDestination}>
            <SelectTrigger data-testid="ratecard-destination" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{destinations.map((d) => <SelectItem key={d.code} value={d.code}>{d.name} ({d.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Container</label>
          <SizeToggle />
        </div>
      </div>

      {!lane ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="ratecard-noresult">No rate on file for this port pair.</div>
      ) : (
        <div data-testid="ratecard-result">
          <div className="border border-border bg-white p-6 mb-8">
            <div className="flex items-center gap-2 text-muted-foreground mb-2"><MapPin className="h-4 w-4" strokeWidth={1.5} /><span className="font-mono-plex text-[11px] tracking-widest uppercase">Port pair · {size}′ {size === "20" ? "GP" : "HC"}</span></div>
            <div className="font-display text-2xl sm:text-3xl tracking-tight">{lane.origin_name} <span className="text-muted-foreground text-xl">({lane.origin_code})</span> <span className="text-muted-foreground mx-1">→</span> {lane.destination_name} <span className="text-muted-foreground text-xl">({lane.destination_code})</span></div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground font-mono-plex">
              {lane.ocean.service_type && <span>Service: <span className="text-foreground/80">{lane.ocean.service_type}</span></span>}
              {lane.ocean.transit_days != null && <span>Transit: <span className="text-foreground/80">{lane.ocean.transit_days} days</span></span>}
              {lane.ocean.validity && <span>Validity: <span className="text-foreground/80">{lane.ocean.validity}</span></span>}
              {lane.volume?.total_teu != null && <span>Est. volume: <span className="text-foreground/80">{lane.volume.total_teu} TEU</span></span>}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 items-stretch">
            <Block title="Origin Charges" icon={Warehouse} section={lane.origin} size={size} testid="ratecard-origin-block"
              terms={[{ label: "Free time", value: lane.origin.free_time_days != null ? `${lane.origin.free_time_days} days` : null },
                      { label: "Detention / day", value: lane.origin.detention }, { label: "POL prefilling", value: lane.origin.prefilling }]} />
            <Block title="Ocean Freight" icon={Ship} section={lane.ocean} size={size} testid="ratecard-ocean-block"
              terms={[{ label: "Fuel index", value: lane.ocean.fuel_index }, { label: "Service type", value: lane.ocean.service_type },
                      { label: "Origin free time", value: lane.ocean.free_time_days != null ? `${lane.ocean.free_time_days} days` : null },
                      { label: "Rate validity", value: lane.ocean.validity }]} />
            <Block title="Destination Charges" icon={Anchor} section={lane.destination} size={size} testid="ratecard-destination-block"
              terms={[{ label: "Free time", value: lane.destination.free_time_days != null ? `${lane.destination.free_time_days} days` : null },
                      { label: "Detention / day", value: lane.destination.detention }, { label: "Notes", value: lane.destination.comments }]} />
          </div>

          {grand && (
            <div className="border-2 border-[#0072B8] bg-[#0072B8]/[0.04] p-6" data-testid="ratecard-grand-total">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-[#0072B8] mb-3"><Container className="h-4 w-4" /><span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Estimated grand total · {size}′ · all-in (AUD)</span></div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Origin</div>
                      <div className="tabular-nums text-foreground/70">{money(grand.o, grand.oc)}</div>
                      <div className="tabular-nums font-medium text-foreground">{aud(grand.oA)}</div>
                    </div>
                    <div>
                      <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Ocean</div>
                      <div className="tabular-nums text-foreground/70">{money(grand.f, grand.fc)}</div>
                      <div className="tabular-nums font-medium text-foreground">{aud(grand.fA)}</div>
                    </div>
                    <div>
                      <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Destination</div>
                      <div className="tabular-nums text-foreground/70">{money(grand.d, grand.dc)}</div>
                      <div className="tabular-nums font-medium text-foreground">{aud(grand.dA)}</div>
                    </div>
                  </div>
                </div>
                <div className="lg:text-right lg:border-l lg:border-[#0072B8]/25 lg:pl-8">
                  <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Grand total (est.)</div>
                  <div className="font-display text-4xl tracking-tight text-foreground tabular-nums" data-testid="ratecard-grand-total-value">{aud(grand.total)}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">per {size}′ container</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground/70 mt-4 leading-relaxed">
                Converted to AUD at live OANDA rates{fx_meta?.as_of ? ` (as of ${fx_meta.as_of})` : ""}:
                {" "}{Object.entries(fx).filter(([c]) => c !== "AUD").map(([c, r]) => `${c}/AUD ${r}`).join(" · ")}.
                {" "}{data.meta.note}
              </p>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground/70 mt-6 leading-relaxed max-w-4xl">{data.meta.issued} · {data.meta.provider} · {data.meta.contact}</p>
        </div>
      )}
    </div>
  );
};

const MarketRates = ({ data }) => {
  const mr = data.market_rates;
  const lanes = mr.meta.lanes.filter((ln) => mr.rows.some((r) => r.rates[ln.key]));
  const [lane, setLane] = useState(lanes[0]?.key || "");
  const [size, setSize] = useState("40");
  const field = size === "20" ? "v20" : "v40";
  const rows = mr.rows
    .filter((r) => r.rates[lane])
    .map((r) => ({ carrier: r.carrier, contract: r.contract, vf: r.validity_from, vt: r.validity_to, v20: r.rates[lane].v20, v40: r.rates[lane].v40 }))
    .sort((a, b) => (a[field] ?? Infinity) - (b[field] ?? Infinity));
  const cheapest = rows.reduce((m, r) => (isNum(r[field]) ? (m == null ? r[field] : Math.min(m, r[field])) : m), null);
  const activeLane = lanes.find((l) => l.key === lane);

  return (
    <div data-testid="market-rates">
      <p className="text-sm text-muted-foreground max-w-3xl mb-6">{mr.meta.subtitle}</p>
      <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 items-end" data-testid="mr-lookup">
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Trade lane</label>
          <Select value={lane} onValueChange={setLane}>
            <SelectTrigger data-testid="mr-lane-select" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{lanes.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Container</label>
          <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="mr-size-toggle">
            {["20", "40"].map((s) => (
              <button key={s} onClick={() => setSize(s)} data-testid={`mr-size-${s}`}
                className={`px-4 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${size === s ? "bg-[#0072B8] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}>
                {s}′ {s === "20" ? "GP" : "HC"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border bg-white overflow-x-auto" data-testid="mr-table">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border bg-slate-50/70">
          <Ship className="h-4 w-4 text-[#0072B8]" strokeWidth={1.75} />
          <span className="font-mono-plex text-[11px] tracking-widest uppercase font-semibold">{activeLane?.label} · Port to Port (US$)</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Carrier</th>
              <th className="px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Contract · Validity</th>
              <th className={`px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase font-semibold text-right ${size === "20" ? "text-[#0072B8]" : "text-muted-foreground"}`}>20′</th>
              <th className={`px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase font-semibold text-right ${size === "40" ? "text-[#0072B8]" : "text-muted-foreground"}`}>40′</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const best = isNum(r[field]) && r[field] === cheapest;
              return (
                <tr key={i} className={`border-t border-slate-100 ${best ? "bg-[#0072B8]/[0.05]" : ""}`} data-testid={`mr-row-${i}`}>
                  <td className="px-4 py-2.5 font-medium text-foreground">
                    {r.carrier}
                    {best && <span className="ml-2 font-mono-plex text-[9px] tracking-widest uppercase text-[#0072B8] border border-[#0072B8]/40 rounded px-1.5 py-0.5">Lowest</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.contract || "—"}<span className="block text-[10px] text-muted-foreground/70">{r.vf} → {r.vt}</span></td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${size === "20" ? "font-semibold text-foreground bg-[#0072B8]/[0.04]" : "text-muted-foreground"}`}>{isNum(r.v20) ? money(r.v20, "USD") : "—"}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${size === "40" ? "font-semibold text-foreground bg-[#0072B8]/[0.04]" : "text-muted-foreground"}`}>{isNum(r.v40) ? money(r.v40, "USD") : "—"}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No carrier rates on file for this lane.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground/70 mt-4 leading-relaxed max-w-4xl">{mr.meta.note} · {mr.meta.as_of}</p>
    </div>
  );
};

export default function RateCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("nac");

  useEffect(() => {
    api.get("/commercial/rate-card").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="ratecard-error">Unable to load the rate card. Please try again.</div>;
  if (!data) return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse" data-testid="ratecard-loading">Loading rate card…</div>;

  const TABS = [{ key: "nac", label: "Indicative Rates for NAC only" }, { key: "market", label: "Market Rates" }, { key: "xeneta", label: "Xeneta vs NAC" }];

  return (
    <div data-testid="rate-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border mb-8">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ratecard-tab-${t.key}`}
            className={`relative px-4 py-2.5 text-xs font-mono-plex tracking-widest uppercase transition-colors ${tab === t.key ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t.label}
            {tab === t.key && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-[#0072B8]" />}
          </button>
        ))}
      </div>
      {tab === "nac" && <NacRates data={data} />}
      {tab === "market" && <MarketRates data={data} />}
      {tab === "xeneta" && <XenetaVsNac />}
    </div>
  );
}
